import { useMemo, useState } from 'react'

import { useUsersAccessRows } from '../../hooks/useUsersAccessRows'
import type { UsersAccessFilterValuesI } from '../../types/usersAccess'
import {
  countUsersAccessFilters,
  filterUsersAccessRows,
  getUsersAccessFilterCount,
} from '../../utils/usersAccess'
import { useLightMode } from '@/hooks/useLightMode'
import { useTablePagination } from '@/hooks/useTablePagination'

import { SETTINGS_TABLE_PAGE_SIZE } from '@/features/settings/dark/constants/settingsTable'

import { UsersAccessTable } from './UsersAccessTable'
import { UsersAccessToolbar } from './UsersAccessToolbar'

const EMPTY_FILTERS: UsersAccessFilterValuesI = {
  roles: [],
  accountStates: [],
  accountTypes: [],
}

export const UsersAccessSection = () => {
  const colorScheme = useLightMode()
  const { isError, isLoading, retry, rows } = useUsersAccessRows()
  const [search, setSearch] = useState('')
  const [filters, setFilters] =
    useState<UsersAccessFilterValuesI>(EMPTY_FILTERS)

  const counts = useMemo(() => countUsersAccessFilters(rows), [rows])
  const filteredRows = useMemo(
    () => filterUsersAccessRows(rows, search, filters),
    [rows, search, filters]
  )
  const {
    pageCount,
    pageIndex,
    pageSize,
    resetToFirstPage,
    setPageIndex,
    setPageSize,
  } = useTablePagination({
    defaultPageSize: SETTINGS_TABLE_PAGE_SIZE,
    totalRows: filteredRows.length,
  })
  const pagedRows = useMemo(
    () => filteredRows.slice(pageIndex * pageSize, (pageIndex + 1) * pageSize),
    [filteredRows, pageIndex, pageSize]
  )

  return (
    <>
      <UsersAccessToolbar
        colorScheme={colorScheme}
        counts={counts}
        filters={filters}
        onApplyFilters={(nextFilters) => {
          setFilters(nextFilters)
          resetToFirstPage()
        }}
        onSearchChange={(value) => {
          setSearch(value)
          resetToFirstPage()
        }}
        search={search}
      />
      <UsersAccessTable
        colorScheme={colorScheme}
        hasActiveFilters={
          Boolean(search.trim()) || getUsersAccessFilterCount(filters) > 0
        }
        isError={isError}
        isLoading={isLoading}
        onPageChange={setPageIndex}
        onPageSizeChange={setPageSize}
        onRetry={retry}
        pageCount={pageCount}
        pageIndex={pageIndex}
        pageSize={pageSize}
        rows={pagedRows}
        total={filteredRows.length}
      />
    </>
  )
}
